import { useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { BrandLogo } from "@/components/brand-logo";
import { authClient } from "@/lib/auth-client";
import { useRouter } from "@/lib/navigation";
import { meQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

export const Route = createFileRoute("/login")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.fetchQuery(meQuery).catch(() => null);
    if (me?.staff) throw redirect({ to: "/" });
  },
  component: LoginPage,
});

const LINK_ERRORS: Record<string, string> = {
  INVALID_TOKEN: "That sign-in link has expired or was already used. Ask for a new one below.",
  EXPIRED_TOKEN: "That sign-in link has expired. Ask for a new one below.",
  new_user_signup_disabled: "That email does not have an account here. Accounts are created by an admin.",
  sso: "That sign-in link did not work. Use your password or ask for a new link.",
};

function LoginPage() {
  useTitle("Sign in");
  const { error } = Route.useSearch();
  const linkError = error ? (LINK_ERRORS[error] ?? "That sign-in link did not work. Ask for a new one below.") : null;

  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-2xl bg-surface border border-border p-8 shadow-sm">
        <div className="flex flex-col items-center gap-2">
          <BrandLogo className="w-[220px] h-auto dark:hidden" />
          <span className="hidden dark:block"><BrandLogo variant="tile" className="w-[196px]" /></span>
          <span className="mt-1 font-heading text-[11px] font-semibold uppercase tracking-widest text-accent-dark">Dashboard</span>
        </div>
        <LoginForm linkError={linkError} />
        <p className="mt-5 text-[11px] text-muted text-center">
          Private. Mars Med Supply and Digital King only. Accounts are created by an admin.
        </p>
      </div>
    </div>
  );
}

type Mode = "password" | "magic" | "forgot";

const inputClass =
  "w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm text-foreground outline-none focus:border-accent-dark";
const buttonClass =
  "w-full rounded-lg bg-primary hover:bg-primary-hover disabled:opacity-60 text-primary-foreground text-sm font-semibold py-2.5 transition-colors";
const linkClass = "text-xs text-accent-dark hover:underline";

function LoginForm({ linkError }: { linkError: string | null }) {
  const router = useRouter();
  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(linkError);
  const [sent, setSent] = useState<null | "magic" | "forgot">(null);

  function switchTo(next: Mode) {
    setMode(next);
    setError(null);
  }

  async function signInPassword(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { error } = await authClient.signIn.email({ email, password });
    if (error) {
      setError(error.status === 401 ? "That email and password do not match." : (error.message ?? "Sign-in failed."));
      setLoading(false);
      return;
    }
    router.refresh();
    router.replace("/");
  }

  async function sendMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { error } = await authClient.signIn.magicLink({ email, callbackURL: "/", errorCallbackURL: "/login" });
    setLoading(false);
    if (error) setError(error.message ?? "Could not send the link.");
    else setSent("magic");
  }

  async function sendReset(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { error } = await authClient.requestPasswordReset({ email, redirectTo: "/reset-password" });
    setLoading(false);
    if (error) setError(error.message ?? "Could not send the reset email.");
    else setSent("forgot");
  }

  if (sent) {
    return (
      <div className="mt-6 text-center">
        <h1 className="font-heading text-lg font-semibold text-foreground">Check your email</h1>
        <p className="mt-2 text-sm text-muted">
          {sent === "magic" ? (
            <>
              If <span className="font-medium">{email}</span> has an account, a sign-in link is on its way. It works once, for one hour.
            </>
          ) : (
            <>
              If <span className="font-medium">{email}</span> has an account, a link to choose a new password is on its way.
            </>
          )}
        </p>
        <button
          type="button"
          onClick={() => {
            setSent(null);
            switchTo("password");
          }}
          className={`mt-4 ${linkClass}`}
        >
          Back to sign in
        </button>
      </div>
    );
  }

  const emailField = (
    <input
      type="email"
      required
      autoComplete="email"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      placeholder="you@marsmedsupply.com"
      aria-label="Email"
      className={inputClass}
    />
  );

  return (
    <>
      <h1 className="mt-6 font-heading text-lg font-semibold text-foreground text-center">
        {mode === "forgot" ? "Reset your password" : "Team sign in"}
      </h1>

      {mode === "password" && (
        <form onSubmit={signInPassword} className="mt-6 space-y-3">
          {emailField}
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            aria-label="Password"
            className={inputClass}
          />
          <button type="submit" disabled={loading} className={buttonClass}>
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
      )}

      {mode === "magic" && (
        <form onSubmit={sendMagicLink} className="mt-6 space-y-3">
          {emailField}
          <button type="submit" disabled={loading} className={buttonClass}>
            {loading ? "Sending..." : "Email me a sign-in link"}
          </button>
        </form>
      )}

      {mode === "forgot" && (
        <form onSubmit={sendReset} className="mt-6 space-y-3">
          <p className="text-sm text-muted">Enter your email and we will send a link to choose a new password.</p>
          {emailField}
          <button type="submit" disabled={loading} className={buttonClass}>
            {loading ? "Sending..." : "Send reset link"}
          </button>
        </form>
      )}

      {error && <p className="mt-3 text-xs text-red-500 text-center">{error}</p>}

      <div className="mt-4 flex flex-col items-center gap-2">
        {mode !== "password" && (
          <button type="button" onClick={() => switchTo("password")} className={linkClass}>
            Sign in with a password
          </button>
        )}
        {mode !== "magic" && (
          <button type="button" onClick={() => switchTo("magic")} className={linkClass}>
            Email me a sign-in link instead
          </button>
        )}
        {mode !== "forgot" && (
          <button type="button" onClick={() => switchTo("forgot")} className={linkClass}>
            Forgot password
          </button>
        )}
      </div>
    </>
  );
}
